import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { toast, Toaster } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, BadgeCheck, Printer, ListChecks, History } from "lucide-react";
import JustificationsView from "../components/admin/JustificationsView";
import { downloadBlob } from "../lib/download";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "");

function SlotCell({ info }) {
  if (!info.done) {
    return (
      <div className="flex items-center justify-center gap-1.5 text-[var(--late)] text-xs font-medium">
        <XCircle size={14} /> Non fait
      </div>
    );
  }
  return (
    <div className="text-center text-xs">
      <div className="inline-flex items-center gap-1.5 text-[var(--ok)] font-medium">
        <CheckCircle2 size={14} /> Fait {fmtTime(info.done_at)}
      </div>
      <div className="mt-0.5 text-[var(--ink-soft)]">{info.done_by_name || "—"}</div>
      <div className="mt-0.5 font-mono-spec">
        {info.absent_count > 0 ? <span className="text-[var(--late)]">{info.absent_count} abs.</span> : <span className="text-[var(--muted)]">0 abs.</span>}
        {" · "}
        {info.late_count > 0 ? <span className="text-[var(--just)]">{info.late_count} ret.</span> : <span className="text-[var(--muted)]">0 ret.</span>}
      </div>
    </div>
  );
}

const REFRESH_INTERVAL_MS = 60000;
const keyOf = (r) => `${r.class_id}|${r.student_id}|${r.slot}`;

function ViewTab({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition ${
        active ? "border-[var(--maroon)] text-[var(--maroon)]" : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function OverviewTable({ loading, data, date, onDownload }) {
  if (loading || !data) {
    return <div className="text-[var(--ink-soft)]">Chargement...</div>;
  }
  if (data.rows.length === 0) {
    return <div className="text-[var(--ink-soft)]">Aucune classe.</div>;
  }
  return (
    <table className="w-full text-sm" data-testid="appels-overview-table">
      <thead>
        <tr className="border-b border-[var(--parchment-2)] text-left">
          <th className="py-3 eyebrow !text-[var(--ink-soft)] font-medium">Classe</th>
          {data.slots.map((s) => (
            <th key={s} className="py-3 eyebrow !text-[var(--ink-soft)] font-medium text-center">{data.slot_labels[s]}</th>
          ))}
          <th className="py-3 eyebrow !text-[var(--ink-soft)] font-medium text-center">Feuille papier</th>
        </tr>
      </thead>
      <tbody>
        {data.rows.map((r) => (
          <tr key={r.class.id} className="border-b border-[var(--parchment-2)]/60" data-testid={`appels-overview-row-${r.class.id}`}>
            <td className="py-4 font-medium">{r.class.name}</td>
            {data.slots.map((s) => (
              <td key={s} className="py-4 px-2">
                <SlotCell info={r.slots[s]} />
              </td>
            ))}
            <td className="py-4 text-center">
              <button
                className="btn-secondary !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1.5"
                onClick={() => onDownload(r.class)}
                title={`Feuille d'appel imprimable — ${r.class.name} (${date})`}
                data-testid={`print-sheet-btn-${r.class.id}`}
              >
                <Printer size={13} /> PDF
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function JustificationCell({ r, motifs, setMotifs, busyKey, onJustify }) {
  const k = keyOf(r);
  if (r.status !== "absent") {
    return <span className="text-xs text-[var(--muted)]">—</span>;
  }
  if (r.justified) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--ok)] font-medium" data-testid={`justified-badge-${r.student.id}-${r.slot}`}>
          <BadgeCheck size={14} /> Justifiée · {r.justification}
        </span>
        <button
          className="btn-secondary !py-1 !px-2 text-xs"
          onClick={() => onJustify(r, false)}
          disabled={busyKey === k}
          data-testid={`unjustify-btn-${r.student.id}-${r.slot}`}
        >
          Retirer
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="text"
        placeholder="Motif (maladie, RDV médical...)"
        value={motifs[k] || ""}
        onChange={(e) => setMotifs((m) => ({ ...m, [k]: e.target.value }))}
        className="px-2.5 py-1.5 w-56 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-xs"
        data-testid={`justify-motif-input-${r.student.id}-${r.slot}`}
      />
      <button
        className="btn-primary !py-1.5 !px-3 text-xs"
        onClick={() => onJustify(r, true)}
        disabled={busyKey === k}
        data-testid={`justify-btn-${r.student.id}-${r.slot}`}
      >
        Justifier
      </button>
    </div>
  );
}

function SlotGroup({ slot, rows, motifs, setMotifs, busyKey, onJustify }) {
  const abs = rows.filter((r) => r.status === "absent").length;
  return (
    <div className="mt-5" data-testid={`bulletin-slot-${slot}`}>
      <div className="flex items-center gap-3 px-3 py-2 bg-[var(--parchment)]">
        <span className="font-mono-spec font-semibold text-[var(--maroon)]">{rows[0].slot_label}</span>
        <span className="text-xs text-[var(--ink-soft)]">
          {abs} absent{abs > 1 ? "s" : ""} · {rows.length - abs} retard{rows.length - abs > 1 ? "s" : ""}
        </span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={keyOf(r)} className="border-b border-[var(--parchment-2)]/60" data-testid={`appel-day-row-${r.student.id}-${r.slot}`}>
              <td className="py-3 font-medium w-64">{r.student.last_name.toUpperCase()} {r.student.first_name}</td>
              <td className="py-3 text-[var(--ink-soft)] w-40">{r.class?.name || "—"}</td>
              <td className="py-3 text-center w-24">
                <span className="px-2 py-0.5 text-xs font-medium text-white" style={{ background: r.status === "absent" ? "var(--late)" : "var(--just)" }}>
                  {r.status === "absent" ? "Absent" : "Retard"}
                </span>
              </td>
              <td className="py-3">
                <JustificationCell r={r} motifs={motifs} setMotifs={setMotifs} busyKey={busyKey} onJustify={onJustify} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DaySection({ dayRows, date, motifs, setMotifs, busyKey, onJustify }) {
  const absents = dayRows.filter((r) => r.status === "absent");
  const nonJust = absents.filter((r) => !r.justified).length;
  const slots = [...new Set(dayRows.map((r) => r.slot))];

  const downloadBulletin = () =>
    downloadBlob("/admin/appel-day.pdf", `bulletin-absences-${date}.pdf`, "Bulletin du jour téléchargé", "Échec du téléchargement du bulletin", { date });

  return (
    <section className="mt-8 bg-white border border-[var(--parchment-2)] p-6" data-testid="appel-day-section">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">Bulletin récapitulatif du jour — mis à jour après chaque appel</div>
          <h2 className="font-serif-display text-2xl font-semibold mt-1">Absences & retards, toutes classes</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm font-mono-spec" data-testid="bulletin-totals">
            <span className="text-[var(--late)] font-semibold">{absents.length} abs.</span>
            {" · "}
            <span className="text-[var(--just)] font-semibold">{dayRows.length - absents.length} ret.</span>
            {nonJust > 0 && <span className="ml-2 px-2 py-0.5 bg-[var(--late)] text-white text-xs">{nonJust} non justifiée{nonJust > 1 ? "s" : ""}</span>}
          </div>
          <button className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5" onClick={downloadBulletin} data-testid="bulletin-pdf-btn">
            <Printer size={13} /> Bulletin PDF
          </button>
        </div>
      </div>
      {dayRows.length === 0 ? (
        <div className="mt-4 text-sm text-[var(--ink-soft)] italic">Aucune absence ni retard relevé ce jour.</div>
      ) : (
        <div className="overflow-x-auto" data-testid="appel-day-table">
          {slots.map((slot) => (
            <SlotGroup key={slot} slot={slot} rows={dayRows.filter((r) => r.slot === slot)} motifs={motifs} setMotifs={setMotifs} busyKey={busyKey} onJustify={onJustify} />
          ))}
        </div>
      )}
    </section>
  );
}

function useAppelsOverview() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [dayRows, setDayRows] = useState([]);
  const [motifs, setMotifs] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data }, day] = await Promise.all([
        api.get("/admin/appel-overview", { params: { date } }),
        api.get("/admin/appel-day", { params: { date } }),
      ]);
      setData(data);
      setDayRows(day.data.rows);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const justify = async (r, justified) => {
    const k = keyOf(r);
    const motif = (motifs[k] || "").trim();
    if (justified && !motif) {
      toast.error("Indiquez le motif de la justification");
      return;
    }
    setBusyKey(k);
    try {
      await api.put("/admin/appel/justify", {
        class_id: r.class_id,
        student_id: r.student_id,
        date,
        slot: r.slot,
        justified,
        motif,
      });
      toast.success(justified ? "Absence justifiée" : "Justification retirée");
      setMotifs((m) => ({ ...m, [k]: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyKey(null);
    }
  };

  return { date, setDate, data, dayRows, motifs, setMotifs, busyKey, loading, justify };
}

function AppelsHeader({ view, data, date, setDate }) {
  return (
    <div className="mt-6 flex flex-wrap items-end justify-between gap-6 pb-8 border-b border-[var(--parchment-2)]">
      <div>
        <div className="eyebrow">Direction · Suivi des appels</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Appels & justifications</h1>
        <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
          Vérifiez en un coup d'œil quelles classes ont fait l'appel à chacun des 5 créneaux
          (08h10, 13h00, 14h00, 16h45, étude du soir), justifiez les absences, et consultez l'historique de l'année.
        </p>
      </div>
      <div className="flex items-center gap-4">
        {view === "day" && data && (
          <div className="text-sm" data-testid="appels-overview-counter">
            <span className="font-mono-spec font-semibold text-[var(--maroon)]">{data.done_count}</span>
            <span className="text-[var(--ink-soft)]"> / {data.expected_count} appels faits</span>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <span className="eyebrow">Date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none font-mono-spec text-sm"
            data-testid="appels-overview-date"
          />
        </label>
      </div>
    </div>
  );
}

export default function AppelsOverview() {
  const [view, setView] = useState("day");
  const { date, setDate, data, dayRows, motifs, setMotifs, busyKey, loading, justify } = useAppelsOverview();

  const downloadSheet = (klass) =>
    downloadBlob(
      `/classes/${klass.id}/appel-sheet.pdf`,
      `appel-${klass.name.replace(/ /g, "-")}-${date}.pdf`,
      `Feuille d'appel ${klass.name} téléchargée`,
      "Échec du téléchargement de la feuille d'appel",
      { date }
    );

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="appels-overview-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <AppelsHeader view={view} data={data} date={date} setDate={setDate} />

        {/* Onglets */}
        <div className="mt-8 flex gap-1 border-b border-[var(--parchment-2)]" data-testid="appels-tabs">
          <ViewTab active={view === "day"} onClick={() => setView("day")} icon={<ListChecks size={16} />} label="Appels du jour" testId="tab-appels-day" />
          <ViewTab active={view === "justifs"} onClick={() => setView("justifs")} icon={<History size={16} />} label="Historique des justifications" testId="tab-justifications" />
        </div>

        {view === "justifs" ? (
          <div className="mt-6">
            <JustificationsView />
          </div>
        ) : (
          <>
            <div className="mt-6 bg-white border border-[var(--parchment-2)] p-6 overflow-x-auto">
              <OverviewTable loading={loading} data={data} date={date} onDownload={downloadSheet} />
            </div>
            <DaySection dayRows={dayRows} date={date} motifs={motifs} setMotifs={setMotifs} busyKey={busyKey} onJustify={justify} />
          </>
        )}
      </main>
    </div>
  );
}
